import React, { useState, useEffect } from 'react';
import { 
  ProjectCreationAnalytics, 
  ProjectCreationMetrics, 
  AnalyticsDateRange 
} from '../../services/projectCreationAnalytics';

interface DateRangeOption {
  label: string;
  value: string;
  getDates: () => AnalyticsDateRange;
}

interface AnalyticsDashboardState {
  metrics: ProjectCreationMetrics | null;
  blockingReasons: Array<{ reason: string; count: number; percentage: number }>;
  conversionFunnel: {
    totalUsers: number;
    usersWhoAttempted: number;
    usersWhoSucceeded: number;
    usersWhoRetried: number;
    averageAttemptsPerUser: number;
  } | null;
  loading: boolean;
  error: string | null;
  selectedDateRange: string;
  customDateRange: AnalyticsDateRange;
}

const ProjectCreationAnalyticsDashboard: React.FC = () => {
  const [state, setState] = useState<AnalyticsDashboardState>({
    metrics: null,
    blockingReasons: [],
    conversionFunnel: null,
    loading: true,
    error: null,
    selectedDateRange: '30d',
    customDateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      end: new Date()
    }
  });

  const dateRangeOptions: DateRangeOption[] = [
    {
      label: 'Last 7 days',
      value: '7d',
      getDates: () => ({
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Last 30 days',
      value: '30d',
      getDates: () => ({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Last 90 days',
      value: '90d',
      getDates: () => ({
        start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Custom',
      value: 'custom',
      getDates: () => state.customDateRange
    }
  ];

  const loadAnalytics = async (dateRange?: AnalyticsDateRange) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const [metrics, blockingReasons, conversionFunnel] = await Promise.all([
        ProjectCreationAnalytics.getMetrics(dateRange),
        ProjectCreationAnalytics.getBlockingReasons(dateRange),
        ProjectCreationAnalytics.getConversionFunnel(dateRange)
      ]);

      setState(prev => ({
        ...prev,
        metrics,
        blockingReasons,
        conversionFunnel,
        loading: false
      }));
    } catch (error) {
      console.error('Error loading analytics:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load analytics data. Please try again.'
      }));
    }
  };

  useEffect(() => {
    const selectedOption = dateRangeOptions.find(option => option.value === state.selectedDateRange);
    if (selectedOption) {
      loadAnalytics(selectedOption.getDates());
    }
  }, [state.selectedDateRange]);

  const handleDateRangeChange = (value: string) => {
    setState(prev => ({ ...prev, selectedDateRange: value }));
  };

  const handleCustomDateChange = (field: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    setState(prev => ({
      ...prev,
      customDateRange: {
        ...prev.customDateRange,
        [field]: newDate
      }
    }));

    // If custom range is selected, reload data
    if (state.selectedDateRange === 'custom') {
      const updatedRange = {
        ...state.customDateRange,
        [field]: newDate
      };
      loadAnalytics(updatedRange);
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (num: number): string => {
    return `${num.toFixed(1)}%`;
  };

  if (state.loading) {
    return (
      <div className="analytics-dashboard-loading">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading analytics...</span>
            </div>
            <p className="text-muted">Loading project creation analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="analytics-dashboard-error">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Analytics Error
          </h4>
          <p className="mb-3">{state.error}</p>
          <button 
            className="btn btn-outline-danger"
            onClick={() => loadAnalytics()}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-creation-analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-1">
              <i className="bi bi-graph-up me-2"></i>
              Project Creation Analytics
            </h3>
            <p className="text-muted mb-0">
              Monitor project creation attempts and access control effectiveness
            </p>
          </div>
          
          {/* Date Range Selector */}
          <div className="date-range-controls">
            <div className="d-flex gap-2 align-items-center">
              <label htmlFor="dateRange" className="form-label mb-0 me-2">
                Time Period:
              </label>
              <select
                id="dateRange"
                className="form-select"
                value={state.selectedDateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                style={{ width: 'auto' }}
              >
                {dateRangeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Custom Date Range Inputs */}
            {state.selectedDateRange === 'custom' && (
              <div className="custom-date-range mt-2">
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={state.customDateRange.start.toISOString().split('T')[0]}
                    onChange={(e) => handleCustomDateChange('start', e.target.value)}
                  />
                  <span className="text-muted">to</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={state.customDateRange.end.toISOString().split('T')[0]}
                    onChange={(e) => handleCustomDateChange('end', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      {state.metrics && (
        <div className="metrics-grid mb-4">
          <div className="row g-3">
            <div className="col-md-3">
              <div className="card metric-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="card-subtitle mb-2 text-muted">Total Attempts</h6>
                      <h3 className="card-title mb-0">{formatNumber(state.metrics.totalAttempts)}</h3>
                    </div>
                    <div className="metric-icon">
                      <i className="bi bi-cursor-fill text-primary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card metric-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="card-subtitle mb-2 text-muted">Success Rate</h6>
                      <h3 className="card-title mb-0 text-success">
                        {formatPercentage(state.metrics.conversionRate)}
                      </h3>
                    </div>
                    <div className="metric-icon">
                      <i className="bi bi-check-circle-fill text-success"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card metric-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="card-subtitle mb-2 text-muted">Blocked Attempts</h6>
                      <h3 className="card-title mb-0 text-warning">
                        {formatNumber(state.metrics.blockedAttempts.total)}
                      </h3>
                    </div>
                    <div className="metric-icon">
                      <i className="bi bi-shield-fill-exclamation text-warning"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card metric-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="card-subtitle mb-2 text-muted">Avg Points (Blocked)</h6>
                      <h3 className="card-title mb-0">
                        {formatNumber(Math.round(state.metrics.averagePointsOfBlockedUsers))}
                      </h3>
                    </div>
                    <div className="metric-icon">
                      <i className="bi bi-star-fill text-info"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Blocking Reasons Chart */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-pie-chart-fill me-2"></i>
                Blocking Reasons
              </h5>
            </div>
            <div className="card-body">
              {state.blockingReasons.length > 0 ? (
                <div className="blocking-reasons-chart">
                  {state.blockingReasons.map((reason, index) => (
                    <div key={reason.reason} className="reason-item mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="reason-label">{reason.reason}</span>
                        <div className="reason-stats">
                          <span className="badge bg-secondary me-2">
                            {formatNumber(reason.count)}
                          </span>
                          <span className="text-muted">
                            {formatPercentage(reason.percentage)}
                          </span>
                        </div>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className={`progress-bar ${index === 0 ? 'bg-danger' : index === 1 ? 'bg-warning' : 'bg-info'}`}
                          style={{ width: `${reason.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-info-circle mb-2 d-block" style={{ fontSize: '2rem' }}></i>
                  <p>No blocking data available for the selected period</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-funnel-fill me-2"></i>
                User Conversion Funnel
              </h5>
            </div>
            <div className="card-body">
              {state.conversionFunnel ? (
                <div className="conversion-funnel">
                  <div className="funnel-step mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="step-label">Total Users</span>
                      <span className="step-value">
                        {formatNumber(state.conversionFunnel.totalUsers)}
                      </span>
                    </div>
                    <div className="progress mt-1" style={{ height: '6px' }}>
                      <div className="progress-bar bg-primary" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  
                  <div className="funnel-step mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="step-label">Attempted Creation</span>
                      <span className="step-value">
                        {formatNumber(state.conversionFunnel.usersWhoAttempted)}
                      </span>
                    </div>
                    <div className="progress mt-1" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-info" 
                        style={{ 
                          width: `${state.conversionFunnel.totalUsers > 0 
                            ? (state.conversionFunnel.usersWhoAttempted / state.conversionFunnel.totalUsers) * 100 
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="funnel-step mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="step-label">Successfully Created</span>
                      <span className="step-value">
                        {formatNumber(state.conversionFunnel.usersWhoSucceeded)}
                      </span>
                    </div>
                    <div className="progress mt-1" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-success" 
                        style={{ 
                          width: `${state.conversionFunnel.totalUsers > 0 
                            ? (state.conversionFunnel.usersWhoSucceeded / state.conversionFunnel.totalUsers) * 100 
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="funnel-step">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="step-label">Retried After Block</span>
                      <span className="step-value">
                        {formatNumber(state.conversionFunnel.usersWhoRetried)}
                      </span>
                    </div>
                    <div className="progress mt-1" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-warning" 
                        style={{ 
                          width: `${state.conversionFunnel.totalUsers > 0 
                            ? (state.conversionFunnel.usersWhoRetried / state.conversionFunnel.totalUsers) * 100 
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Average Attempts per User</span>
                      <span className="fw-bold">
                        {state.conversionFunnel.averageAttemptsPerUser.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-info-circle mb-2 d-block" style={{ fontSize: '2rem' }}></i>
                  <p>No funnel data available for the selected period</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      {state.metrics && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="bi bi-table me-2"></i>
                  Detailed Breakdown
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="stat-item">
                      <div className="stat-label text-muted">Blocked by Points Only</div>
                      <div className="stat-value h5 mb-0 text-warning">
                        {formatNumber(state.metrics.blockedAttempts.byPoints)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-item">
                      <div className="stat-label text-muted">Blocked by LinkedIn Only</div>
                      <div className="stat-value h5 mb-0 text-info">
                        {formatNumber(state.metrics.blockedAttempts.byLinkedIn)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-item">
                      <div className="stat-label text-muted">Blocked by Both</div>
                      <div className="stat-value h5 mb-0 text-danger">
                        {formatNumber(state.metrics.blockedAttempts.byBoth)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-item">
                      <div className="stat-label text-muted">Successful Creations</div>
                      <div className="stat-value h5 mb-0 text-success">
                        {formatNumber(state.metrics.successfulCreations)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCreationAnalyticsDashboard;